// MetricModal "Kırılım" bölümleri (spec §6.4): (1) toplam/gösterge düğümünde grup listesi (boyut,
// tamlık, kural, üye sayısı) + "+ Kırılım grubu ekle" (BreakdownGroupModal — üst bileşen) + not
// "katalogda formül yazılmaz"; (2) grup düğümünde tamlık + toplama kuralı düzenleme — değişince
// etki önizlemesi (`GET …/breakdown/preview-metadata`, R10: "N müşteride K değer raporlanan"),
// kayıt aynı PATCH ile (taslak `breakdown`). Jargon yok (sum/avg/slug) — kopya MODAL ile aynı.

import { useEffect, useState } from "react";
import type { KpiBreakdown, KpiMetric } from "@mahir/shared";
import { previewKpiBreakdownMetadata } from "../../api/kpi";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { type CatalogNode, type CatalogView } from "../../lib/kpi-catalog-breakdown";
import { btnSmall, fieldStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.catalog;
const M = KPI_BREAKDOWN_COPY.modal;
const ROLLUPS: readonly KpiBreakdown["rollup"][] = ["sum", "avg", "none"];
const help = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;
const radioRow = { display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" } as const;
const radioInput = { marginTop: 4, width: 18, height: 18 } as const;

function GroupLine(props: { view: CatalogView; group: CatalogNode }): JSX.Element {
  const g = props.group;
  return (
    <>
      <strong style={{ fontWeight: 600 }}>{g.label}</strong>
      <span style={help}>
        {" "}
        · {KPI_BREAKDOWN_COPY.dimensionName(g.breakdown?.dimension, g.label)} ·{" "}
        {C.groupLine(
          props.view.membersOf(g.id).length,
          g.breakdown?.complete ?? true,
          g.breakdown?.rollup ?? "sum",
        )}
      </span>
    </>
  );
}

export function TotalBreakdownPanel(props: {
  metric: KpiMetric;
  view: CatalogView;
  disabled: boolean;
  onAddGroup?: ((totalId: string) => void) | undefined;
}): JSX.Element {
  const groups = props.view.groupsOf(props.metric.id);
  return (
    <section
      aria-label={C.panelTitle}
      className="rounded-lg p-3 mb-3"
      style={{ border: "1px solid var(--color-border)" }}
      data-testid="kpi-metric-breakdown-panel"
    >
      <div style={labelStyle}>{C.panelTitle}</div>
      {groups.length === 0 ? <div style={help}>{C.panelEmpty}</div> : null}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {groups.map((g) => (
          <li key={g.id} style={{ padding: "4px 0" }}>
            <GroupLine view={props.view} group={g} />
            <ul style={{ listStyle: "none", margin: 0, padding: "0 0 0 16px" }}>
              {props.view.membersOf(g.id).map((mem) => (
                <li
                  key={mem.id}
                  style={{ padding: "2px 0" }}
                  data-testid="kpi-metric-breakdown-member"
                >
                  <span>{mem.label}</span>
                  {props.onAddGroup ? (
                    <button
                      type="button"
                      style={{ ...btnSmall, minHeight: 44, marginLeft: 8 }}
                      disabled={props.disabled}
                      aria-label={C.addGroupUnderMember(mem.label)}
                      data-testid="kpi-metric-breakdown-add-member-group"
                      onClick={() => props.onAddGroup?.(mem.id)}
                    >
                      {C.addGroup}
                    </button>
                  ) : null}
                  <ul style={{ listStyle: "none", margin: 0, padding: "0 0 0 16px" }}>
                    {props.view.groupsOf(mem.id).map((sub) => (
                      <li key={sub.id} style={{ padding: "2px 0" }}>
                        <GroupLine view={props.view} group={sub} />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <div style={{ ...help, marginTop: 4 }}>{C.panelNote}</div>
      {props.onAddGroup ? (
        <button
          type="button"
          style={{ ...btnSmall, minHeight: 44, marginTop: 8 }}
          disabled={props.disabled}
          data-testid="kpi-metric-breakdown-add-group"
          onClick={() => props.onAddGroup?.(props.metric.id)}
        >
          {C.addGroup}
        </button>
      ) : null}
    </section>
  );
}

export function GroupMetaSection(props: {
  groupId: string;
  value: KpiBreakdown;
  original: KpiBreakdown | null;
  disabled: boolean;
  onChange: (next: KpiBreakdown) => void;
}): JSX.Element {
  const { value, original } = props;
  const changed =
    original !== null && (original.complete !== value.complete || original.rollup !== value.rollup);
  const [preview, setPreview] = useState<
    | { state: "idle" }
    | { state: "loading" }
    | { state: "ok"; clients: number; values: number }
    | {
        state: "failed";
      }
  >({ state: "idle" });

  useEffect(() => {
    if (!changed) {
      setPreview({ state: "idle" });
      return;
    }
    let live = true;
    setPreview({ state: "loading" });
    previewKpiBreakdownMetadata(props.groupId, { complete: value.complete, rollup: value.rollup })
      .then((r) => {
        if (live) setPreview({ state: "ok", clients: r.clients, values: r.values });
      })
      .catch(() => {
        if (live) setPreview({ state: "failed" });
      });
    return () => {
      live = false;
    };
  }, [changed, props.groupId, value.complete, value.rollup]);

  return (
    <div style={fieldStyle} data-testid="kpi-metric-group-meta">
      <span style={labelStyle}>{C.panelTitle}</span>
      <div style={help}>
        {C.dimensionLine(KPI_BREAKDOWN_COPY.dimensionName(value.dimension, value.dimension))}
      </div>
      <label className="flex items-start gap-2" style={{ minHeight: 32 }}>
        <input
          type="checkbox"
          checked={value.complete}
          disabled={props.disabled}
          style={radioInput}
          onChange={(e) => props.onChange({ ...value, complete: e.target.checked })}
        />
        <span>
          {M.complete}
          <br />
          <span style={help}>{M.completeHint}</span>
        </span>
      </label>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={labelStyle}>{M.rollupLegend}</legend>
        {ROLLUPS.map((r) => (
          <label key={r} style={radioRow}>
            <input
              type="radio"
              name="m-group-rollup"
              value={r}
              checked={value.rollup === r}
              disabled={props.disabled}
              style={radioInput}
              onChange={() => props.onChange({ ...value, rollup: r })}
            />
            <span>
              {M.rollup[r]}
              {r === "avg" ? <span style={help}> {M.avgHint}</span> : null}
            </span>
          </label>
        ))}
      </fieldset>
      {original?.complete && !value.complete ? (
        <div
          style={{ ...help, color: "var(--color-warning-text)" }}
          data-testid="kpi-metric-group-meta-complete-off"
        >
          {C.completeOffWarning}
        </div>
      ) : null}
      {preview.state !== "idle" ? (
        <div style={help} aria-live="polite" data-testid="kpi-metric-group-meta-preview">
          {preview.state === "loading"
            ? C.metaPreviewing
            : preview.state === "failed"
              ? C.metaPreviewFailed
              : C.metaPreview(preview.clients, preview.values)}
        </div>
      ) : null}
    </div>
  );
}
